import { formatObservationDate } from '~/components/maud/patients/profile/clinical-profile/clinical-measurement/clinical-measurement-modal/key-vitals/KeyVitalsList/utils';

import { isDateRangeValidExcludingSameDate } from './carePlan.util';
import { getPreferredFullName, getTaskAssigneeName } from './patient.util';
import {
  getActivityChangeKeys,
  getDisplayFieldsList,
} from './patientActivity.util';
import { GOAL_STATUS } from '../constants/goals.constants';

//When two open ended records
export const checkForBothOpenEnded = (
  currentGoalEndDate: string,
  endDate: string,
): boolean => !(currentGoalEndDate || endDate);

//When adding open ended records
export const checkForCurrentOpenEnded = (
  currentGoalStartDate: string,
  startDate: string,
  currentGoalEndDate: string,
  endDate: string,
): boolean =>
  !currentGoalEndDate &&
  endDate &&
  currentGoalStartDate &&
  startDate &&
  !isDateRangeValidExcludingSameDate(endDate, currentGoalStartDate);

//When existing open ended records but adding a record with both start and end date
export const checkForExistingOpenEnded = (
  currentGoalStartDate: string,
  startDate: string,
  currentGoalEndDate: string,
  endDate: string,
): boolean =>
  !endDate &&
  currentGoalStartDate &&
  currentGoalEndDate &&
  startDate &&
  !isDateRangeValidExcludingSameDate(currentGoalEndDate, startDate);
//When both adding and existing records have both start and end date
export const checkForBothDates = (
  currentGoalStartDate: string,
  startDate: string,
  currentGoalEndDate: string,
  endDate: string,
): boolean =>
  currentGoalEndDate &&
  endDate &&
  currentGoalStartDate &&
  startDate &&
  !(
    isDateRangeValidExcludingSameDate(currentGoalEndDate, startDate) ||
    isDateRangeValidExcludingSameDate(endDate, currentGoalStartDate)
  );

export const checkForOverlappingGoalDates = (
  currentExternalId: string,
  currentGoalStartDate: string,
  currentGoalEndDate: string,
  goalsData: PatientWeightGoalType[],
): boolean =>
  //Should not validate against itself so filtering that record out if exists
  {
    return goalsData
      ?.filter(
        ({ observationGoalExternalId }) =>
          observationGoalExternalId !== currentExternalId,
      )
      ?.some(({ goalStartDate, goalEndDate }) => {
        return (
          checkForBothOpenEnded(currentGoalEndDate, goalEndDate) ||
          checkForCurrentOpenEnded(
            currentGoalStartDate,
            goalStartDate,
            currentGoalEndDate,
            goalEndDate,
          ) ||
          checkForExistingOpenEnded(
            currentGoalStartDate,
            goalStartDate,
            currentGoalEndDate,
            goalEndDate,
          ) ||
          checkForBothDates(
            currentGoalStartDate,
            goalStartDate,
            currentGoalEndDate,
            goalEndDate,
          )
        );
      });
  };

export const getActiveGoals = (
  goalsList: PatientWeightGoalType[],
): PatientWeightGoalType[] =>
  goalsList.filter(({ goalStatus }) => goalStatus === GOAL_STATUS.ACTIVE);

export const getNonActiveGoals = (
  goalsList: PatientWeightGoalType[],
): PatientWeightGoalType[] =>
  goalsList.filter(({ goalStatus }) => goalStatus !== GOAL_STATUS.ACTIVE);

export const getActiveAndCompleteGoals = (
  goalsList: PatientWeightGoalType[],
): PatientWeightGoalType[] =>
  goalsList.filter(
    ({ goalStatus }) =>
      goalStatus === GOAL_STATUS.ACTIVE || goalStatus === GOAL_STATUS.COMPLETE,
  );

export const getAssigneeNamesFromIds = (
  ids: string[],
  taskAssignees: FamilyTaskAssignee[],
): string[] => {
  const userNames: string[] = [];
  (ids ?? []).forEach((eachId) => {
    const selectedUser: FamilyTaskAssignee = taskAssignees.find(
      (eachUser) => eachUser.externalId === eachId,
    );
    if (selectedUser) {
      userNames.push(getTaskAssigneeName(selectedUser));
    }
  });
  return userNames;
};

export const getAssigneeIdsFromNames = (
  names: string[],
  taskAssignees: FamilyTaskAssignee[],
): string[] => {
  const ids: string[] = [];
  (names ?? []).forEach((each) => {
    const selectedUser: FamilyTaskAssignee = taskAssignees.find(
      (eachUser) =>
        getPreferredFullName(
          eachUser.chosenName,
          eachUser.firstName,
          eachUser.lastName,
        ) === each,
    );
    if (selectedUser) {
      ids.push(selectedUser.externalId);
    }
  });
  return ids;
};
export enum GoalHistoryChangeType {
  CREATED = 'CREATED',
  NEW_FEATURE = 'NEW_FEATURE',
}

enum GoalHistoryFields {
  END_DATE = 'end_date',
  GOAL_STATUS_ID = 'goal_status_id',
  GOAL_TYPE_ID = 'goal_type_id',
  HIGH_BOUND = 'high_bound',
  LOW_BOUND = 'low_bound',
  START_DATE = 'start_date',
}

const GoalFieldMapper = {
  end_date: 'End date',
  goal_status_id: 'Status',
  goal_type_id: 'Type',
  high_bound: 'High boundary',
  low_bound: 'Low boundary',
  start_date: 'Start date',
};
const getSeparateActivityForEachField = (
  activity: PatientActivityListObject,
): GoalActivityType[] => {
  const { after, before, createdAt, createdBy, operator } = activity;

  if (operator === GoalHistoryChangeType.CREATED) {
    return [
      {
        activityExternalId:
          after?.observation_goal_external_id ||
          before?.observation_goal_external_id,
        fieldChanged: null,
        newValue: null,
        previousValue: null,
        type: operator,
        updatedAt: createdAt,
        updatedBy: getPreferredFullName(
          createdBy?.chosenName,
          createdBy?.firstName,
          createdBy?.lastName,
        ),
      },
    ];
  }
  const changedKeys: string[] = getActivityChangeKeys(before, after);
  return getDisplayFieldsList(GoalFieldMapper, changedKeys).map((key) => ({
    activityExternalId:
      after?.observation_goal_external_id ||
      before?.observation_goal_external_id,
    fieldChanged: key,
    newValue: after?.[key],
    previousValue: before?.[key],
    type: operator,
    updatedAt: createdAt,
    updatedBy: getPreferredFullName(
      createdBy?.chosenName,
      createdBy?.firstName,
      createdBy?.lastName,
    ),
  }));
};

export const getGoalActivityHistory = (
  goalActivityList: PatientActivityListObject[],
): GoalActivityType[] =>
  goalActivityList.reduce(
    (activityList: GoalActivityType[], activity: PatientActivityListObject) => [
      ...activityList,
      ...getSeparateActivityForEachField(activity),
    ],
    [],
  );

const getGoalActivityFieldName = (fieldChanged: string): string => {
  return GoalFieldMapper[fieldChanged];
};

const formatGoalHistoryValue = (
  value: string | number,
  fieldChanged: string,
  goalStatuses: GoalStatus[],
  goalTypes: GoalType[],
): string => {
  if (!value) return '';
  if (
    fieldChanged === GoalHistoryFields.END_DATE ||
    fieldChanged === GoalHistoryFields.START_DATE
  )
    return formatObservationDate(value as string);
  if (
    fieldChanged === GoalHistoryFields.LOW_BOUND ||
    fieldChanged === GoalHistoryFields.HIGH_BOUND
  )
    return `${value}lbs`;
  if (fieldChanged === GoalHistoryFields.GOAL_STATUS_ID)
    return goalStatuses?.find(({ goalStatusId }) => goalStatusId === value)
      ?.goalStatusName;
  if (fieldChanged === GoalHistoryFields.GOAL_TYPE_ID)
    return goalTypes?.find(({ goalTypeId }) => goalTypeId === value)
      ?.goalTypeName;
  return value as string;
};

export const getGoalHistoryChangeText = (
  activity: GoalActivityType,
  goalStatuses: GoalStatus[],
  goalTypes: GoalType[],
): string => {
  const { fieldChanged, newValue, previousValue, type }: GoalActivityType =
    activity;
  if (type === GoalHistoryChangeType.CREATED) {
    return 'Goal added';
  }
  const formattedNewValue = formatGoalHistoryValue(
    newValue,
    fieldChanged,
    goalStatuses,
    goalTypes,
  );
  const formattedPreviousValue = formatGoalHistoryValue(
    previousValue,
    fieldChanged,
    goalStatuses,
    goalTypes,
  );
  const prevValueDisplay = formattedPreviousValue
    ? `from ${formattedPreviousValue} `
    : '';
  return `${getGoalActivityFieldName(
    fieldChanged,
  )} edited ${prevValueDisplay} to ${formattedNewValue}`;
};
